/**
 * ClubNetworkLanding - Neutral landing page for the VBB Pet Club Network
 * Shown for preview domains and unknown/unrecognized domains
 * Directs users to discover clubs or join a specific one
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Globe, 
  Users, 
  Dog, 
  Heart,
  ArrowRight,
  Lightbulb,
  Crown
} from 'lucide-react';

const ClubNetworkLanding = () => {
  const navigate = useNavigate();

  const featuredClubs = [
    {
      name: 'All-American Dog Club',
      slug: 'all-american-dog-club',
      tagline: 'Every Dog Is a Champion',
      country: 'USA',
      flag: '🇺🇸',
      color: '#c9762b',
      members: '2+',
      type: 'flagship'
    },
    {
      name: 'Club Canino de España',
      slug: 'club-canino-espana',
      tagline: 'Cada Perro es un Campeón',
      country: 'Spain',
      flag: '🇪🇸',
      color: '#AA151B',
      members: '1+',
      type: 'standard'
    },
    {
      name: 'WorldPets Global',
      slug: 'worldpets',
      tagline: 'Connecting Pet Lovers Worldwide',
      country: 'Global',
      flag: '🌍',
      color: '#1e3a5f',
      members: '1+',
      type: 'network'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm mb-6">
              <Globe className="w-4 h-4" />
              <span>Very Big Box Pet Club Network</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Find Your Pack
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
              Join a community of pet lovers. Discover clubs by location, breed, or interest. 
              Every club offers resources, community, and a voice for you and your pets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => navigate('/clubs')}
                className="bg-white text-slate-900 hover:bg-slate-100"
              >
                <Globe className="w-5 h-5 mr-2" />
                Browse All Clubs
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/suggest-club')}
                className="border-white/30 text-white hover:bg-white/10"
              >
                <Lightbulb className="w-5 h-5 mr-2" />
                Suggest a Club
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Clubs */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Featured Clubs</h2>
          <p className="text-slate-600 max-w-xl mx-auto">
            Start your journey with one of our established communities
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {featuredClubs.map((club) => (
            <Card 
              key={club.slug}
              className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate(`/club/${club.slug}`)}
            >
              <div 
                className="h-3"
                style={{ backgroundColor: club.color }}
              />
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{club.flag}</span>
                  <div>
                    <h3 className="font-bold text-lg group-hover:text-primary transition-colors">
                      {club.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">{club.tagline}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {club.members} members
                    </span>
                  </div>
                  {club.type === 'flagship' && (
                    <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                      <Crown className="w-3 h-3" />
                      Flagship
                    </span>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  className="w-full mt-4 group-hover:bg-primary/5"
                >
                  Visit Club
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8">
          <Button 
            variant="outline" 
            size="lg"
            onClick={() => navigate('/clubs')}
          >
            View All 11 Clubs
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Value Props */}
      <div className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Dog className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">Every Dog Is a Champion</h3>
              <p className="text-muted-foreground">
                Purebred or mixed, we celebrate all dogs equally. No papers required.
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">Community First</h3>
              <p className="text-muted-foreground">
                Connect with local pet owners, share experiences, and support each other.
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">Make a Difference</h3>
              <p className="text-muted-foreground">
                Support rescues, reduce ownership costs, and advocate for responsible pet care.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Join?</h2>
        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
          Pick a club that fits your location or interests. Membership is free during beta!
        </p>
        <Button 
          size="lg"
          onClick={() => navigate('/clubs')}
          className="px-8"
        >
          Find Your Club
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>

      {/* Footer note */}
      <div className="border-t py-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Part of the Very Big Box Pet Club Network</p>
        </div>
      </div>
    </div>
  );
};

export default ClubNetworkLanding;
